import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ExternalLink, Github, Calendar, Users, Zap, Play, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { projectsData } from '../data/projectsData';
import YouTubeEmbed from '../components/YouTubeEmbed';
import '../styles/ProjectDetail.css';

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const imageCounterRef = useRef(0);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  
  const project = projectsData.find(p => p.slug === slug);
  
  // Helper function to check if URL is YouTube
  const isYouTubeUrl = (url: string): boolean => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (project?.contentFile) {
      imageCounterRef.current = 0; // Reset counter
      fetch(project.contentFile)
        .then(response => response.text())
        .then(content => {
          setMarkdownContent(content);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading project content:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [project]);

  // Auto-scroll to selected thumbnail
  useLayoutEffect(() => {
    if (thumbnailsRef.current && thumbnailRefs.current[currentMediaIndex]) {
      const container = thumbnailsRef.current;
      const thumbnail = thumbnailRefs.current[currentMediaIndex];
      
      if (thumbnail) {
        const containerRect = container.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        
        // Check if thumbnail is out of view
        if (thumbnailRect.left < containerRect.left || thumbnailRect.right > containerRect.right) {
          // Scroll the thumbnail into view, centered if possible
          thumbnail.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }
    }
  }, [currentMediaIndex]);

  if (!project) {
    return (
      <>
        <Helmet>
          <title>Project Not Found - Pedro Sugasti</title>
          <meta name="description" content="The project you're looking for doesn't exist." />
        </Helmet>
        <div className="project-detail-error">
          <h2>Project not found</h2>
          <button 
            onClick={() => navigate('/work')} 
            className="btn btn-primary"
          >
            <ArrowLeft size={16} /> Back to Work
          </button>
        </div>
      </>
    );
  }

  const handleImageClick = (src: string, index?: number) => {
    setSelectedImage(src);
    if (project?.media && index !== undefined) {
      setSelectedImageIndex(index);
    } else if (project?.media) {
      const foundIndex = project.media.findIndex(m => m === src);
      if (foundIndex !== -1) {
        setSelectedImageIndex(foundIndex);
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };
  
  const handleModalPrev = () => {
    if (project?.media && selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(project.media[newIndex]);
    }
  };
  
  const handleModalNext = () => {
    if (project?.media && selectedImageIndex < project.media.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(project.media[newIndex]);
    }
  };

  const nextMedia = () => {
    if (project?.media && currentMediaIndex < project.media.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  const prevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
    }
  };

  const goToMedia = (index: number) => {
    setCurrentMediaIndex(index);
  };

  const customRenderers = {
    h1: ({ children }: any) => <h1 className="article-title-main">{children}</h1>,
    h2: ({ children }: any) => <h2 className="section-title">{children}</h2>,
    h3: ({ children }: any) => <h3 className="subsection-title">{children}</h3>,
    p: ({ children }: any) => <p className="article-text">{children}</p>,
    img: ({ src, alt }: any) => {
      // Parse alignment from alt text: "align-left|Description" or "align-right|Description"
      let alignment = 'right'; // default
      let caption = alt;
      
      if (alt && alt.includes('|')) {
        const parts = alt.split('|');
        if (parts[0].trim() === 'align-left') {
          alignment = 'left';
          caption = parts[1] ? parts[1].trim() : '';
        } else if (parts[0].trim() === 'align-right') {
          alignment = 'right';
          caption = parts[1] ? parts[1].trim() : '';
        }
      }
      
      const floatClass = alignment === 'left' ? 'float-left' : 'float-right';
      
      console.log(`Image: ${alignment.toUpperCase()} - ${caption}`);
      
      return (
        <figure className={`article-figure-compact ${floatClass}`}>
          <img 
            src={src} 
            alt={caption}
            onClick={() => handleImageClick(src)}
            className="clickable-image"
          />
          {caption && <figcaption>{caption}</figcaption>}
        </figure>
      );
    },
    blockquote: ({ children }: any) => (
      <blockquote className="article-quote">
        {children}
      </blockquote>
    ),
    em: ({ children }: any) => (
      <div className="article-note">
        <p><em>{children}</em></p>
      </div>
    ),
    a: ({ href, children }: any) => {
      // If it's a YouTube link, render as embedded video
      if (href && isYouTubeUrl(href)) {
        return (
          <div className="markdown-video">
            <YouTubeEmbed url={href} title={children || 'Video'} preview="thumbnail" />
          </div>
        );
      }
      // Regular link
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
  };

  if (loading) {
    return (
      <div className="project-detail">
        <div className="loading-state">
          <p>Loading project content...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{project.title} - {project.subtitle} | Pedro Sugasti</title>
        <meta name="description" content={project.description} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content={`${project.title} - ${project.subtitle}`} />
        <meta property="og:description" content={project.description} />
        <meta property="og:image" content={`https://pedrosugasti.com${project.image}`} />
        <meta property="og:url" content={`https://pedrosugasti.com/work/${project.slug}`} />
        <meta property="og:type" content="article" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${project.title} - ${project.subtitle}`} />
        <meta name="twitter:description" content={project.description} />
        <meta name="twitter:image" content={`https://pedrosugasti.com${project.image}`} />
        
        {/* Article specific tags */}
        {project.year && <meta property="article:published_time" content={`${project.year}-01-01`} />}
        <meta property="article:author" content="Pedro Sugasti" />
        {project.categories.map(category => (
          <meta key={category} property="article:tag" content={category} />
        ))}
        
        <link rel="canonical" href={`https://pedrosugasti.com/work/${project.slug}`} />
      </Helmet>
      
      <motion.div 
        className="project-detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="article-header"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="article-hero-image">
          <img src={project.image || '/default.png'} alt={project.title} />
          <div className="article-overlay">
            <div className="article-categories">
              {project.categories.map((category, index) => (
                <span key={index} className="article-category">{category}</span>
              ))}
            </div>
            {project.year && (
              <span className="article-date">
                <Calendar size={16} /> {project.year}
              </span>
            )}
            <div className="article-header-content">
              {project.subtitle && (
                <div className="article-subtitle">{project.subtitle}</div>
              )}
              <h1 className="article-title">{project.title}</h1>
            </div>
            <button 
              onClick={() => navigate('/work')} 
              className="back-to-projects"
              title="Back to Work"
              aria-label="Back to Work"
            >
              <ArrowLeft size={20} />
            </button>
          </div>
        </div>
      </motion.div>

      <motion.article 
        className="article-content"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <p className="article-lead">{project.description}</p>
        <div className="article-body">
          {markdownContent ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={customRenderers}
            >
              {markdownContent}
            </ReactMarkdown>
          ) : (
            <div className="no-content">
              <p>No detailed content available for this project.</p>
            </div>
          )}
        </div>
      </motion.article>

      <div className="project-content-wrapper">
        {project.liveUrl && isYouTubeUrl(project.liveUrl) && (
          <div className="project-video">
            <h3><Play size={20} /> Media</h3>
            <YouTubeEmbed url={project.liveUrl} title={`${project.title} Demo`} preview="card" />
          </div>
        )}

        {project.media && project.media.length > 0 && (
          <div className="project-media-gallery">
            <h3><Play size={20} /> Gallery</h3>
            
            <div className="gallery-container">
              <div className="gallery-main">
                <div className="gallery-image-container">
                  <button 
                    className="gallery-nav-btn gallery-nav-prev" 
                    onClick={prevMedia}
                    disabled={currentMediaIndex === 0}
                  >
                    <ChevronLeft size={24} />
                  </button>
                  
                  <div className="gallery-image-wrapper">
                    <img 
                      src={project.media[currentMediaIndex]} 
                      alt={`${project.title} - Image ${currentMediaIndex + 1}`}
                      onClick={() => handleImageClick(project.media![currentMediaIndex], currentMediaIndex)}
                      className="gallery-main-image clickable-image"
                    />
                    <div className="gallery-counter">
                      {currentMediaIndex + 1} / {project.media.length}
                    </div>
                  </div>
                  
                  <button 
                    className="gallery-nav-btn gallery-nav-next" 
                    onClick={nextMedia}
                    disabled={currentMediaIndex === project.media.length - 1}
                  >
                    <ChevronRight size={24} />
                  </button>
                </div>
                
                <div className="gallery-thumbnails" ref={thumbnailsRef}>
                  {project.media.map((image, index) => (
                    <button
                      key={index}
                      ref={(el) => {
                        thumbnailRefs.current[index] = el;
                      }}
                      className={`gallery-thumbnail ${index === currentMediaIndex ? 'active' : ''}`}
                      onClick={() => goToMedia(index)}
                    >
                      <img src={image} alt={`Thumbnail ${index + 1}`} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="project-extras">

        {(project.liveUrl && !isYouTubeUrl(project.liveUrl)) || project.githubUrl ? (
          <div className="project-links">
            {project.liveUrl && !isYouTubeUrl(project.liveUrl) && (
              <a 
                href={project.liveUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <ExternalLink size={18} />
                <span>View Live Project</span>
              </a>
            )}
            {project.githubUrl && (
              <a 
                href={project.githubUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <Github size={18} />
                <span>View Source Code</span>
              </a>
            )}
          </div>
        ) : null}

        <div className="project-technologies">
          <h3><Zap size={20} /> Technologies</h3>
          <div className="tech-tags">
            {project.technologies.map(tech => (
              <span key={tech} className="tech-tag">
                <span>{tech}</span>
                <Zap size={14} />
              </span>
            ))}
          </div>
        </div>

        {project.team && (
          <div className="project-team">
            <h3><Users size={20} /> Team</h3>
            <p>{project.team}</p>
          </div>
        )}

        {project.sources && project.sources.length > 0 && (
          <div className="project-sources">
            <h3><BookOpen size={20} /> Sources & References</h3>
            <div className="sources-list">
              {project.sources.map((source, index) => (
                <a
                  key={index}
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="source-link"
                >
                  <ExternalLink size={16} />
                  <span>{source.title}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedImage && (
        <div className="image-modal" onClick={handleCloseModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            {project?.media && project.media.length > 1 && (
              <>
                <button 
                  className="modal-nav-btn modal-prev" 
                  onClick={handleModalPrev}
                  disabled={selectedImageIndex === 0}
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  className="modal-nav-btn modal-next" 
                  onClick={handleModalNext}
                  disabled={selectedImageIndex === project.media.length - 1}
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}
            <img src={selectedImage} alt="Full size view" />
            {project?.media && project.media.length > 1 && (
              <div className="modal-counter">
                {selectedImageIndex + 1} / {project.media.length}
              </div>
            )}
            <button className="image-modal-close" onClick={handleCloseModal}>
              ×
            </button>
          </div>
        </div>
      )}
      </motion.div>
    </>
  );
};

export default ProjectDetail;